import { isValidNum } from "../../utils/mathUtils.js";
import { pointInPolygon } from "../../utils/pointInPolygon.js";
import type { Zone } from "./zone.js";
import type { VertexInfo } from "../commonSubSchemas/vertexInfo.js";

export const getCenter = (zone: Zone): number[] => {
    const CenterPoint = [0, 0];
    let divisor = 0;
    let corners = [];
    if (zone.polygons && zone.polygons[0]) {
        corners = zone.polygons[0];
    } else {
        return [0, 0];
    }
    corners.forEach((v) => {
        const lat: number = v.lat ?? v[0] ?? v.lattitude;
        const lng: number = v.lng ?? v[1] ?? v.longitude;
        if (isValidNum(lat) && isValidNum(lng)) {
            CenterPoint[0] += lat;
            CenterPoint[1] += lng;
            divisor++;
        } else {
            console.warn("Zone polygon contains an invalid corner", { corners });
        }
    });
    if (divisor === 0) {
        divisor = 1;
    }
    CenterPoint[0] /= divisor;
    CenterPoint[1] /= divisor;
    return CenterPoint;
};

function sqr(x: number) {
    return x * x;
}

function distBetweenPoints(a: number[], b: number[]) {
    return sqr(a[0] - b[0]) + sqr(a[1] - b[1]);
}

function distToSegmentSquared(p: number[], lineStart: number[], lineEnd: number[]) {
    const l2 = distBetweenPoints(lineStart, lineEnd);
    if (l2 === 0) {
        return distBetweenPoints(p, lineStart);
    }
    let t =
        ((p[0] - lineStart[0]) * (lineEnd[0] - lineStart[0]) + (p[1] - lineStart[1]) * (lineEnd[1] - lineStart[1])) /
        l2;

    t = Math.max(0, Math.min(1, t));
    return distBetweenPoints(p, [
        lineStart[0] + t * (lineEnd[0] - lineStart[0]),
        lineStart[1] + t * (lineEnd[1] - lineStart[1]),
    ]);
}

export const containsPoint = (zone: Zone, point: VertexInfo, softBorder = false): boolean => {
    if (!zone.polygons || !zone.polygons[0] || zone.polygons[0].length <= 0) {
        return false;
    }
    const vertices = zone.polygons[0].map((vertex) => [vertex.lat, vertex.lng]);
    const listPoint = [point.lat, point.lng];

    const boundingBox = [
        [zone.polygons[0][0].lat, zone.polygons[0][0].lng],
        [zone.polygons[0][0].lat, zone.polygons[0][0].lng],
    ];
    zone.polygons[0].forEach((vertex) => {
        boundingBox[0][0] = Math.min(boundingBox[0][0], vertex.lat);
        boundingBox[0][1] = Math.min(boundingBox[0][1], vertex.lng);

        boundingBox[1][0] = Math.max(boundingBox[1][0], vertex.lat);
        boundingBox[1][1] = Math.max(boundingBox[1][1], vertex.lng);
    });
    const dimensions = [boundingBox[1][0] - boundingBox[0][0], boundingBox[1][1] - boundingBox[0][1]];
    let margin = 0;
    if (softBorder) {
        const maxMargin = 0.0003;
        const minMargin = 0.000005;
        margin = ((dimensions[0] + dimensions[1]) / 2) * 0.08;
        margin = Math.min(maxMargin, Math.max(margin, minMargin));
    }
    const outerBoundingBox = [
        [boundingBox[0][0] - margin, boundingBox[0][1] - margin],
        [boundingBox[1][0] + margin, boundingBox[1][1] + margin],
    ];
    const pointInsideOuterBoundingBox =
        listPoint[0] >= outerBoundingBox[0][0] &&
        listPoint[0] <= outerBoundingBox[1][0] &&
        listPoint[1] >= outerBoundingBox[0][1] &&
        listPoint[1] <= outerBoundingBox[1][1];
    if (!pointInsideOuterBoundingBox) {
        return false;
    }
    const insideBaseShape = pointInPolygon(listPoint, vertices);
    if (insideBaseShape) {
        return true;
    }
    const marginSquared = margin * margin;

    for (let i = 0; i < zone.polygons[0].length; i++) {
        const i2 = (i + 1) % zone.polygons[0].length;
        const closeToLineSegment =
            distToSegmentSquared(
                listPoint,
                [zone.polygons[0][i].lat, zone.polygons[0][i].lng],
                [zone.polygons[0][i2].lat, zone.polygons[0][i2].lng],
            ) < marginSquared;
        if (closeToLineSegment) {
            return true;
        }
    }
    return false;
};
